import moment from 'moment'
import insightClient from './config'

export type SalaryCalculationItem = {
  date: string
  totalPayout: number | null
  currentCompensation: number
  payouts: {
    date: string
    amount: number
  }[]
}

export const fetchSalaryCalculation = async (
  practitionerReferenceId: string,
  periodStart: string,
  periodEnd: string
): Promise<SalaryCalculationItem[]> => {
  const { data } = await insightClient.get('/api/payout', {
    params: {
      practitionerReferenceId,
      periodStart: moment(periodStart).format('YYYY-MM-DD'),
      periodEnd: moment(periodEnd).format('YYYY-MM-DD'),
    },
  })
  return data
}

export type Compensation = {
  absenceCompensation: number
  hours: number
}

export type TotalContractedHoursPreviousMonths = {
  totalHours: number
  months: {
    month: string
    contractedWeeklyWorkHours: number
    effectiveWorkHours: number
  }[]
}

export type TotalRevenuePreviousMonths = {
  totalRevenue: number
  months: { month: string; total: number }[]
}

type NPS = {
  npsBonusRate: number
  npsRangeEnd: number
  npsRangeStart: number
  personalNpsBonusScore: number
}

type TeamBonus = {
  annualTeamBonusPayout: number
  teamAnnualRevenue: number
  teamBonusRate: number
  teamBonusRelatedAmount: number
  teamRevenueThreshold: number
}

export type CompensationResponse = {
  annualPayout?: { teamBonus: TeamBonus | null }
  bonusRate: {
    totalBonusRate: number
    bonusRate: number
    nps: NPS
  }
  discountRate: number
  fixedPayout: number
  guaranteedPayout: number | null
  month: string
  currentMonthTreatmentRevenue: number
  monthlyRevenue: number
  pregnancyCompensation: Compensation | null
  publicHolidaysCompensation: Compensation
  revenueThreshold: number
  sicknessCompensation: Compensation
  total: number
  totalBonusEligibleRevenue: number
  totalBonusPayout: number
  totalContractedHoursPreviousMonths: TotalContractedHoursPreviousMonths
  totalRevenuePreviousMonths: TotalRevenuePreviousMonths
  vacationCompensation: Compensation
}

export type PractitionerType = 'dentist' | 'new' | 'chief'

export const fetchCompensation = async (
  practitionerReferenceId: string,
  date: string,
  type: PractitionerType,
  initialCompensation: boolean
): Promise<CompensationResponse> => {
  const { data } = await insightClient.get(`/api/compensation/${practitionerReferenceId}`, {
    params: { month: date, type, initialCompensation },
  })
  return data
}

export type PayoutPractitioner = {
  clinic: {
    referenceId: string
    name: string
    tenant: string
    type: string
    resourceType: string
  }
  contractEnd: string | null
  contractStart: string
  name: string
  referenceId: string
  resourceType: string
  type: string
}

export enum PractitionerStatus {
  ACTIVE = 'ACTIVE',
  USED_TO_BE_ACTIVE = 'USED_TO_BE_ACTIVE',
  DISABLED = 'DISABLED',
}

export const fetchPayoutPractitioners = async (showDeprecated: boolean): Promise<PayoutPractitioner[]> => {
  const practitionerStatuses = showDeprecated
    ? [PractitionerStatus.ACTIVE, PractitionerStatus.USED_TO_BE_ACTIVE, PractitionerStatus.DISABLED]
    : [PractitionerStatus.ACTIVE, PractitionerStatus.USED_TO_BE_ACTIVE]

  const { data } = await insightClient.get('/api/payout/practitioner', {
    params: {
      size: 9999,
      practitionerStatuses,
    },
  })
  return data
}

export const fetchPaymentData = async (date: string): Promise<{ data: Blob; fileName: string }> => {
  const { data, request } = await insightClient.get(`api/payout/finance/${date}`, {
    headers: {
      accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/json',
    },
    responseType: 'blob',
  })
  const fileName = request.getResponseHeader('x-export-filename')

  return { data, fileName }
}
